import { Link } from "wouter";
import { motion } from "framer-motion";
import { useRef } from "react";
import { useInView } from "framer-motion";
import {
  CheckCircle2,
  Clock,
  Sparkles,
  ArrowLeft,
  Zap,
  Shield,
  Mail,
  FolderKanban,
  Bot,
  Palette,
  FlaskConical,
  Rocket,
  Calendar,
  Users,
  Smartphone,
  Puzzle,
  Mic,
  BarChart3,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import LegalFooter from "@/components/legal-footer";
import { Button } from "@/components/ui/button";

/* ─── Types ─────────────────────────────────────────────────────── */

type ChangeType = "feature" | "improvement" | "fix" | "security";

interface Change {
  type: ChangeType;
  text: string;
}

interface Release {
  version: string;
  date: string;
  title: string;
  highlight?: boolean;
  changes: Change[];
}

interface RoadmapItem {
  icon: typeof Rocket;
  title: string;
  description: string;
  status: "soon" | "planned" | "exploring";
}

/* ─── Data ───────────────────────────────────────────────────────── */

const RELEASES: Release[] = [
  {
    version: "0.6",
    date: "Mai 2026",
    title: "Mode démo, landing animée & UX assistant",
    highlight: true,
    changes: [
      { type: "feature", text: "Mode démo activable par variable d'environnement (AUTH_DISABLED) — accès au dashboard sans login pour les déploiements vitrines." },
      { type: "feature", text: "Illustration \"L'IA fait le tri\" remplacée par un réseau de neurones animé avec signaux qui traversent le réseau et sortent triés par catégorie." },
      { type: "improvement", text: "Suggestions \"À surveiller\" dissociées du chat : deux blocs visuels indépendants avec leur propre carte." },
      { type: "improvement", text: "Logo OneNext dans la navbar renvoie désormais vers la landing page depuis toutes les pages de l'application." },
      { type: "improvement", text: "Capture rapide temporairement désactivée sur le dashboard (fonctionnalité en cours de développement)." },
      { type: "feature", text: "Route /landing accessible à tous les visiteurs, y compris les utilisateurs connectés." },
    ],
  },
  {
    version: "0.5",
    date: "Avril 2026",
    title: "Sécurité, légal & infrastructure production",
    changes: [
      { type: "feature", text: "Webhook Clerk production : synchronisation automatique des utilisateurs (création, suppression) via signature svix." },
      { type: "security", text: "Validation de signature cryptographique sur toutes les notifications Clerk entrantes." },
      { type: "feature", text: "Politique de remboursement, DPA et six pages légales complètes (CGU, CGV, mentions, confidentialité, cookies)." },
      { type: "feature", text: "Bannière de consentement cookies conforme RGPD avec trois niveaux (tout accepter / essentiels / personnaliser)." },
      { type: "feature", text: "Export de données personnelles (Article 20 RGPD) et suppression de compte (Article 17) depuis les paramètres." },
      { type: "improvement", text: "Palette de couleurs migrée du vert/sage vers le bleu cohérent sur l'ensemble de l'application." },
    ],
  },
  {
    version: "0.4",
    date: "Mars 2026",
    title: "Tests E2E & qualité",
    changes: [
      { type: "feature", text: "Suite de tests Playwright couvrant les 6 parcours critiques : accueil, triage mail, capture, projets, assistant, paramètres." },
      { type: "feature", text: "Mode E2E : bypass d'authentification Clerk pour les tests automatisés en CI, avec stubs IA et IMAP." },
      { type: "feature", text: "Harnais de test /api/__test/* pour reset, seed et contrôle des stubs en environnement de test." },
      { type: "improvement", text: "Toutes les actions IA couvertes par un coupe-circuit budgétaire mensuel avec alerte à 80 % et blocage à 100 %." },
    ],
  },
  {
    version: "0.3",
    date: "Février 2026",
    title: "Assistant conversationnel & chat IA",
    changes: [
      { type: "feature", text: "Assistant conversationnel avec streaming de réponses, historique de conversation et cartes d'action cliquables." },
      { type: "feature", text: "Analyse \"Maintenant\" : l'assistant scanne mails et tâches à la demande et remonte les points critiques." },
      { type: "feature", text: "Insights proactifs \"À surveiller\" : jusqu'à 3 alertes contextuelles avec échéances, blocages et opportunités." },
      { type: "feature", text: "Transcription vocale : dicter un message à l'assistant directement depuis le microphone." },
      { type: "feature", text: "Page Chat IA avec espaces de travail, documents et conversations persistantes." },
      { type: "improvement", text: "Quota IA quotidien visible dans l'interface avec barre de progression et message d'alerte." },
    ],
  },
  {
    version: "0.2",
    date: "Janvier 2026",
    title: "Triage mail & boîte de réception intelligente",
    changes: [
      { type: "feature", text: "Connexion boîte mail via IMAP standard avec chiffrement AES-256 des identifiants." },
      { type: "feature", text: "Triage automatique : règles déterministes (newsletters, no-reply) puis IA pour les cas ambigus." },
      { type: "feature", text: "Inbox unifiée avec badges de priorité, résumés IA et actions rapides (archiver, créer une tâche, répondre)." },
      { type: "feature", text: "IMAP IDLE : synchronisation en temps réel sans polling intensif." },
      { type: "feature", text: "Étiquettes IA et analyse par lot pour catégoriser les mails existants." },
      { type: "improvement", text: "Dashboard Admin : gestion utilisateurs, logs d'échecs mail, quotas IA et suivi de l'activité." },
    ],
  },
  {
    version: "0.1",
    date: "Décembre 2025",
    title: "Lancement initial — le socle",
    changes: [
      { type: "feature", text: "Dashboard \"Aujourd'hui\" : vue épurée des tâches du jour avec progression et focus unique." },
      { type: "feature", text: "Gestion de projets : tâches, notes, événements et mails liés réunis sur une page par projet." },
      { type: "feature", text: "Page Actions consolidée avec filtres par projet, priorité et échéance." },
      { type: "feature", text: "Authentification sécurisée via Clerk avec connexion Google, Apple et e-mail." },
      { type: "feature", text: "Onboarding guidé en plusieurs étapes : connexion mail, analyse initiale, configuration des préférences." },
      { type: "security", text: "Architecture multi-tenant avec isolation stricte des données par utilisateur." },
    ],
  },
];

const ROADMAP: RoadmapItem[] = [
  {
    icon: Mic,
    title: "Capture rapide",
    description: "Capturez une idée, une todo ou une note vocale en deux secondes. L'IA propose la bonne tâche dans le bon projet.",
    status: "soon",
  },
  {
    icon: Calendar,
    title: "Intégration calendrier",
    description: "Synchronisation Google Calendar et Outlook. Vos événements apparaissent dans la vue Aujourd'hui et l'assistant en tient compte.",
    status: "soon",
  },
  {
    icon: Smartphone,
    title: "Application mobile",
    description: "OneNext en natif sur iOS et Android. Notifications push pour les échéances critiques et capture rapide depuis l'écran d'accueil.",
    status: "planned",
  },
  {
    icon: Users,
    title: "Mode équipe",
    description: "Partagez des projets, déléguez des tâches et suivez l'avancement collectif. Conçu pour les petites équipes et les binômes.",
    status: "planned",
  },
  {
    icon: Puzzle,
    title: "Intégrations tierces",
    description: "Connecteurs natifs avec Notion, Linear, Slack et GitHub. Vos données restent dans OneNext, les actions se propagent partout.",
    status: "planned",
  },
  {
    icon: BarChart3,
    title: "Rapports hebdomadaires",
    description: "Un résumé automatique chaque vendredi : ce que vous avez accompli, ce qui reste en suspens, et les tendances de la semaine.",
    status: "exploring",
  },
];

/* ─── Badge helpers ─────────────────────────────────────────────── */

const TYPE_CONFIG: Record<ChangeType, { label: string; className: string }> = {
  feature:     { label: "Nouveau",       className: "bg-primary/10 text-primary border-primary/20" },
  improvement: { label: "Amélioration",  className: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800" },
  fix:         { label: "Correction",    className: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800" },
  security:    { label: "Sécurité",      className: "bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800" },
};

const STATUS_CONFIG: Record<RoadmapItem["status"], { label: string; className: string; icon: typeof Clock }> = {
  soon:      { label: "Bientôt",      className: "bg-primary/10 text-primary border-primary/20",   icon: Zap },
  planned:   { label: "Planifié",     className: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800", icon: Clock },
  exploring: { label: "En réflexion", className: "bg-muted text-muted-foreground border-border",   icon: FlaskConical },
};

/* ─── Sub-components ─────────────────────────────────────────────── */

function ReleaseCard({ release, index }: { release: Release; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className={`relative rounded-2xl border bg-card p-6 md:p-8 ${
        release.highlight ? "border-primary/30 shadow-lg shadow-primary/5 ring-1 ring-primary/10" : ""
      }`}
    >
      {release.highlight && (
        <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 shadow-sm">
          <Sparkles className="w-3 h-3" />
          Dernière version
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded">
              v{release.version}
            </span>
            <span className="text-xs text-muted-foreground">{release.date}</span>
          </div>
          <h3 className="mt-1.5 text-lg font-semibold tracking-tight">{release.title}</h3>
        </div>
      </div>

      <ul className="space-y-3">
        {release.changes.map((change, i) => {
          const cfg = TYPE_CONFIG[change.type];
          return (
            <li key={i} className="flex items-start gap-3 text-sm">
              <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <span className="text-foreground leading-relaxed">{change.text}</span>
              </div>
              <span
                className={`shrink-0 text-[10px] font-semibold border rounded-full px-2 py-0.5 hidden sm:inline-block ${cfg.className}`}
              >
                {cfg.label}
              </span>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}

function RoadmapCard({ item, index }: { item: RoadmapItem; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const { icon: Icon } = item;
  const status = STATUS_CONFIG[item.status];
  const StatusIcon = status.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.07 }}
      className="rounded-2xl border bg-card p-5 flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon className="w-4.5 h-4.5" />
        </div>
        <span className={`text-[10px] font-semibold border rounded-full px-2.5 py-1 flex items-center gap-1 ${status.className}`}>
          <StatusIcon className="w-3 h-3" />
          {status.label}
        </span>
      </div>
      <div>
        <h4 className="font-semibold text-sm tracking-tight">{item.title}</h4>
        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{item.description}</p>
      </div>
    </motion.div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────── */

export default function ChangelogPage() {
  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto max-w-4xl px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Accueil
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <div className="w-6 h-6 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">
              O
            </div>
            OneNext
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-14">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">
            Changelog & Roadmap
          </p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            Ce qui change, ce qui arrive
          </h1>
          <p className="mt-4 text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Chaque version de OneNext est listée ici. Suivez l'évolution du produit
            et découvrez ce que nous préparons pour la suite.
          </p>
        </motion.div>

        {/* Changelog */}
        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Versions publiées</h2>
          </motion.div>

          <div className="space-y-5">
            {RELEASES.map((release, i) => (
              <ReleaseCard key={release.version} release={release} index={i} />
            ))}
          </div>
        </section>

        {/* Roadmap */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-3 mb-3"
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Rocket className="w-4 h-4" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Roadmap</h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="text-muted-foreground text-sm mb-8 ml-11"
          >
            Fonctionnalités en cours de développement ou à l'étude. L'ordre n'est pas définitif.
          </motion.p>

          {/* Legend */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="flex flex-wrap gap-3 mb-7"
          >
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <span key={key} className={`text-[11px] font-semibold border rounded-full px-2.5 py-1 flex items-center gap-1.5 ${cfg.className}`}>
                  <Icon className="w-3 h-3" />
                  {cfg.label}
                </span>
              );
            })}
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ROADMAP.map((item, i) => (
              <RoadmapCard key={item.title} item={item} index={i} />
            ))}
          </div>
        </section>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-20 rounded-2xl border bg-gradient-to-br from-primary/5 to-primary/10 p-8 text-center"
        >
          <Bot className="w-8 h-8 text-primary mx-auto mb-3" />
          <h3 className="text-lg font-semibold">Une idée pour améliorer OneNext ?</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Partagez vos suggestions — elles alimentent directement notre roadmap.
          </p>
          <Link href="/contact">
            <Button className="mt-5 gap-2">
              Envoyer une suggestion
            </Button>
          </Link>
        </motion.div>
      </main>

      <LegalFooter />
    </div>
  );
}
